import React from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getAuth,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_noborder.png";

const handleGoogleLogin = async () => {
  const auth = getAuth();
  const provider = new GoogleAuthProvider();

  // Enhanced device and browser checks
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isMobileChrome = isMobile && /Chrome/i.test(navigator.userAgent);
  const isMobileFirefox = isMobile && /Firefox/i.test(navigator.userAgent);

  // Prefer signInWithRedirect for most mobile browsers due to varied support for popups
  if (isMobile && !isIOS && (!isMobileChrome || isMobileFirefox)) {
    signInWithRedirect(auth, provider);
  } else {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Authentication error:", error);
      toast.error(error.message);
      if (!isIOS) {
        signInWithRedirect(auth, provider);
      }
    }
  }
};

const handleLogin = async (event) => {
  event.preventDefault(); // Prevent the form from submitting the traditional way

  const email = event.target.email.value;
  const password = event.target.password.value;
  const auth = getAuth();

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log("User signed in:", userCredential.user);
  } catch (error) {
    toast.error(error.message);
    console.error("Error signing in:", error.message);
  }
};


const Login = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-grid flex min-h-[100vh] flex-col justify-center px-4 py-12 text-fg sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-16 w-auto drop-shadow-[0_0_16px_rgb(var(--primary)/0.45)]" src={logo} alt="SpeechCraft" />
        <h2 className="mt-6 text-center font-display text-3xl font-bold uppercase leading-9 tracking-[0.08em] text-fg">
          Sign in to SpeechCraft
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="panel rounded-lg px-6 py-10 shadow-panel sm:px-10">
          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label
                htmlFor="email"
                className="hud-label block"
              >
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full rounded border border-line bg-elevated px-3 py-2.5 text-fg placeholder:text-fg-subtle transition focus:border-primary/70 focus:outline-none focus:ring-1 focus:ring-primary/40 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="hud-label block"
              >
                Password
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="block w-full rounded border border-line bg-elevated px-3 py-2.5 text-fg placeholder:text-fg-subtle transition focus:border-primary/70 focus:outline-none focus:ring-1 focus:ring-primary/40 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div></div>
              <div className="text-sm leading-6">
                <span
                  className="cursor-pointer font-semibold text-primary transition hover:text-primary-strong"
                  onClick={() => {
                    navigate("/forgot-password");
                  }}
                >
                  Forgot Password?
                </span>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded border border-primary bg-primary px-3 py-2 font-display text-sm font-semibold uppercase leading-6 tracking-[0.18em] text-primary-fg transition hover:bg-primary-strong hover:shadow-glow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
              >
                Sign in
              </button>
            </div>
          </form>

          <div>
            <div className="relative mt-10">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-line"></div>
              </div>
              <div className="relative flex justify-center text-sm font-medium leading-6">
                <span className="bg-surface px-4 font-mono text-[11px] uppercase tracking-[0.2em] text-fg-muted">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6 grid gap-4" onClick={handleGoogleLogin}>
              <a
                href="#"
                className="flex w-full items-center justify-center gap-3 rounded border border-line bg-elevated px-3 py-2 text-sm font-semibold text-fg transition hover:border-primary/50 hover:shadow-glow-sm"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                    fill="#EA4335"
                  />
                  <path
                    d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                    fill="#34A853"
                  />
                </svg>
                <span className="text-sm font-semibold leading-6">Google</span>
              </a>

              {/* <a
                href="#"
                className="flex w-full items-center justify-center gap-3 rounded border border-line bg-elevated px-3 py-2 text-sm font-semibold text-fg transition hover:border-primary/50"
              >
                <svg
                  className="h-5 w-5 fill-fg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span className="text-sm font-semibold leading-6">GitHub</span>
              </a> */}
            </div>
            <p className="mt-8 text-center text-sm text-fg-muted">
              Not registered?{" "}
              <Link
                to="/signup"
                className="leading-6 font-semibold text-primary transition hover:text-primary-strong"
              >
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Login;
